import type { SubscribableEntityType } from "@productlane/lib/src/browser"
import { NOTIFICATION_ROW_COPY } from "@productlane/lib/src/notifications/presentation"

const SUBSCRIPTION_SCOPE: Record<SubscribableEntityType, string> = {
	thread: "this thread",
	company: "threads from this company",
	tag: "threads with this tag",
	view: "threads in this view",
	issue: "this issue",
	project: "this project",
	docsArticle: "this article",
}

export const NOTIFICATIONS_COPY = {
	navLabel: "Notifications",
	panelTitle: "Notifications",
	views: {
		inbox: "All",
		unread: "Unread",
		mentions: "Mentions",
	},
	filters: {
		type: "Type",
		reason: "Reason",
		from: "From",
		system: "System",
		noMatchTitle: "No matches",
		noMatchText: "No notifications match these filters",
		selectedCount: {
			types: (count: number) => `${count} types`,
			reasons: (count: number) => `${count} reasons`,
			actorIds: (count: number) => `${count} people`,
		},
	},
	reasons: {
		assigned: "Assigned to you",
		mentioned: "Mentioned",
		admin: "Workspace admin",
		initiator: "Started by you",
		subscribed: "Subscribed",
		topic: "Followed tag or company",
		workspace: "Workspace",
	} as Record<string, string>,
	detail: {
		noUnread: "No unread notifications",
		unreadCount: (count: string, isOne: boolean) =>
			`${count} unread ${isOne ? "notification" : "notifications"}`,
		open: "Open",
		notFound: "This is no longer available",
	},
	actions: {
		markAllRead: "Mark all notifications as read",
		deleteAll: "Delete all notifications",
		deleteRead: "Delete all read notifications",
		deleteCompleted: "Delete notifications for completed issues and threads",
		markRead: "Mark as read",
		markUnread: "Mark as unread",
		delete: "Delete",
		deleteNotification: "Delete notification",
		openInNewTab: "Open in new tab",
		snooze: "Snooze notification...",
		settings: "Notification settings",
		more: "More actions",
	},
	menu: {
		copy: "Copy",
	},
	selection: {
		select: "Select notification",
		count: (count: number) => `${count} selected`,
		clear: "Clear selection",
	},
	typeHints: NOTIFICATION_ROW_COPY.typeHints,
	events: NOTIFICATION_ROW_COPY.events,
	fromActor: NOTIFICATION_ROW_COPY.fromActor,
	fallbackBody: NOTIFICATION_ROW_COPY.fallbackBody,
	empty: {
		inbox: "No notifications",
		unread: "No unreads",
		mentions: "No mentions",
		showAll: "Show all notifications",
	},
	loadMore: "Show older",
	subscribe: {
		subscribe: "Subscribe",
		unsubscribe: "Unsubscribe",
		subscribedTooltip: (entityType: SubscribableEntityType) =>
			`You get notified about ${SUBSCRIPTION_SCOPE[entityType]}`,
		unsubscribedTooltip: (entityType: SubscribableEntityType) =>
			`Get notified about ${SUBSCRIPTION_SCOPE[entityType]}`,
		subscribers: "Subscribers",
		you: "You",
		nobody: "Nobody gets updates on this yet",
		addPeople: "Add people",
		searchPeople: "Search people...",
		noPeople: "No one found",
		others: (count: number) => `${count} others`,
		sources: {
			manual: "Subscribed",
			creator: "Created",
			assignee: "Assigned",
			mentioned: "Mentioned",
			commented: "Commented",
			replied: "Replied",
			owner: "Owner",
			added: "Added",
		} as Record<string, string>,
	},
	toasts: {
		subscribed: "You'll get notified about this",
		unsubscribed: "You won't get notified about this anymore",
		failed: "Couldn't update your notifications. Try again.",
	},
}

export const NOTIFICATION_SETTINGS_COPY = {
	overviewSubtitle: "Choose what Productlane tells you about, and where.",
	pageTitle: "Inbox",
	pageSubtitle: "Pick what shows up in your Productlane inbox.",
	navTitle: "Inbox",
	navDescription: "Choose which events show up in the app.",
	desktopTitle: "Desktop notifications",
	desktopDescription: "Pop up new notifications when this tab is hidden",
	desktopBlocked: "Allow notifications for Productlane in your browser",
	desktopUnsupported: "This browser can't show desktop notifications",
	scopeOptions: {
		unassigned: "Unassigned",
		viewsHeading: "Views",
	},
	saveFailed: "Couldn't save your notification setting. Try again.",
}
